import { LayerSpecification, Map } from "@iventis/mapbox-gl";
import { BehaviorSubject, Subscription } from "rxjs";
import { pairwise } from "rxjs/operators";
import { SitemapVersionType } from "@iventis/domain-model/model/sitemapVersionType";
import { getAddedAndRemovedElementsFromArray } from "@iventis/utilities";
import { Asset } from "@iventis/types/asset";
import { getSitemapMapOrderValue } from "@iventis/sitemaps";
import { Sitemap2d } from "./sitemap-2d";
import { Sitemap3d } from "./sitemap-3d";
import { Sitemap2dDataStore } from "../../../../data-store/basemap/sitemap-2d-data-store";
import { MapState, SitemapVersionLevel } from "../../../../types/store-schema";
import { Sitemap3dDataStore } from "../../../../data-store/basemap/sitemap-3d-data-store";
import { DeckglMapboxIntegration } from "../../deckgl/deckgl-mapbox-integration";
import { IVENTIS_SITEMAP_AREA_SLOT_ID, IVENTIS_SITEMAP_LINE_SLOT_ID, IVENTIS_SITEMAP_RASTER_SLOT_ID, IVENTIS_SITEMAP_TEXT_SLOT_ID } from "../../../../bridge/constants";

export class SitemapHandler {
    private readonly map: Map;

    private readonly store: BehaviorSubject<MapState>;

    private storeSubscription: Subscription;

    private readonly deckglMapboxIntegration: DeckglMapboxIntegration;

    private readonly sitemap2dDataStore: Sitemap2dDataStore;

    private readonly sitemap3dDataStore: Sitemap3dDataStore;

    private sitemaps2d: Record<string, Sitemap2d> = {};

    private sitemaps3d: Record<string, Sitemap3d> = {};

    constructor(map: Map, store: BehaviorSubject<MapState>, deckglMapboxIntegration: DeckglMapboxIntegration, multipleAssetUrlGetter: (ids: string[]) => Promise<Asset[]>) {
        this.map = map;
        this.store = store;
        this.deckglMapboxIntegration = deckglMapboxIntegration;

        this.sitemap2dDataStore = new Sitemap2dDataStore(multipleAssetUrlGetter);
        this.sitemap3dDataStore = new Sitemap3dDataStore(multipleAssetUrlGetter);

        // Listen to state changes
        this.onStoreChange();
    }

    public addSitemapVersionLevelToMap(sitemapVersionLevel: SitemapVersionLevel, scale?: number) {
        if (this.isSitemapVersionLevelOnMap(sitemapVersionLevel.id)) {
            return;
        }

        switch (sitemapVersionLevel.type) {
            case SitemapVersionType.TwoDimensional:
                this.sitemaps2d[sitemapVersionLevel.id] = new Sitemap2d(
                    sitemapVersionLevel,
                    this.sitemap2dDataStore,
                    this.map,
                    { getAboveLayerId: this.getSitemapLayerAboveLayerId.bind(this) },
                    scale
                );
                break;
            case SitemapVersionType.ThreeDimensional:
                this.sitemaps3d[sitemapVersionLevel.id] = new Sitemap3d(sitemapVersionLevel, this.sitemap3dDataStore, this.map, this.deckglMapboxIntegration);
                break;
            default:
                throw new Error(`Sitemap type of ${sitemapVersionLevel.type} is not handled`);
        }
    }

    get areAll2DSitemapsLoaded() {
        return Object.values(this.sitemaps2d).every((sitemap) => sitemap.loaded);
    }

    get has3dSitemap() {
        return Object.keys(this.sitemaps3d).length > 0;
    }

    private onStoreChange() {
        this.storeSubscription = this.store.pipe(pairwise()).subscribe(([previousState, state]) => {
            if (state.selectedSitemapVersionLevels.stamp !== previousState.selectedSitemapVersionLevels.stamp) {
                const { added, removed } = getAddedAndRemovedElementsFromArray(
                    previousState.selectedSitemapVersionLevels.value,
                    state.selectedSitemapVersionLevels.value,
                    (sitemapVersionLevel) => sitemapVersionLevel.id
                );

                // Check if a 3d sitemap has been removed and added (means level has changed)
                const levelChangeFor3dSitemap =
                    removed.some((svl) => svl.type === SitemapVersionType.ThreeDimensional) && added.some((svl) => svl.type === SitemapVersionType.ThreeDimensional);

                if (levelChangeFor3dSitemap) {
                    // Add 2d sitemaps normally
                    removed
                        .filter((sitemapVersionLevel) => sitemapVersionLevel.type === SitemapVersionType.TwoDimensional)
                        .forEach((sitemapVersionLevel) => {
                            this.removeSitemapVersionLevelFromMap(sitemapVersionLevel.id);
                        });

                    added
                        .filter((sitemapVersionLevel) => sitemapVersionLevel.type === SitemapVersionType.TwoDimensional)
                        .forEach((sitemapVersionLevel) => {
                            this.addSitemapVersionLevelToMap(sitemapVersionLevel);
                        });

                    // Adds the new 3d sitemaps and once they have loaded and rendered, will remove the previous 3d sitemaps.
                    this.change3dSitemapLevel(
                        added.filter(({ type }) => type === SitemapVersionType.ThreeDimensional),
                        removed.filter(({ type }) => type === SitemapVersionType.ThreeDimensional)
                    );
                } else {
                    removed.forEach((sitemapVersionLevel) => {
                        this.removeSitemapVersionLevelFromMap(sitemapVersionLevel.id);
                    });

                    added.forEach((sitemapVersionLevel) => {
                        this.addSitemapVersionLevelToMap(sitemapVersionLevel);
                    });
                }
            }
        });
    }

    private isSitemapVersionLevelOnMap(sitemapVersionLevelId: string) {
        return this.sitemaps2d[sitemapVersionLevelId] != null || this.sitemaps3d[sitemapVersionLevelId] != null;
    }

    /** Changes a 3d sitemap level gracefully, will load the next level before removing the previous one */
    private async change3dSitemapLevel(addedLevels: SitemapVersionLevel[], removedLevels: SitemapVersionLevel[]) {
        // Add each  3d sitemap one by one and wait until previously one has loaded
        for (const addedLevel of addedLevels) {
            this.addSitemapVersionLevelToMap(addedLevel);
            await this.waitUntilSitemapHasLoaded(addedLevel.id);
        }

        // Once new 3d sitemaps have been added, remove the old ones
        removedLevels.forEach((level) => {
            this.removeSitemapVersionLevelFromMap(level.id);
        });
    }

    /** Continously checks if a sitemap has been fully loaded */
    private async waitUntilSitemapHasLoaded(sitemapLevelId: string, waitBetweenChecksMs = 250) {
        const sitemap = this.sitemaps2d[sitemapLevelId] ?? this.sitemaps3d[sitemapLevelId];

        await new Promise<void>((resTimeout) =>
            setTimeout(() => {
                resTimeout();
            }, waitBetweenChecksMs)
        );

        // If sitemap has not loaded yet, check again
        if (!sitemap?.loaded) {
            await this.waitUntilSitemapHasLoaded(sitemapLevelId);
        }
    }

    private removeSitemapVersionLevelFromMap(sitemapVersionLevelId: string) {
        if (!this.isSitemapVersionLevelOnMap(sitemapVersionLevelId)) {
            return;
        }

        if (this.sitemaps2d[sitemapVersionLevelId] != null) {
            this.sitemaps2d[sitemapVersionLevelId].remove();
            delete this.sitemaps2d[sitemapVersionLevelId];
        }

        if (this.sitemaps3d[sitemapVersionLevelId] != null) {
            this.sitemaps3d[sitemapVersionLevelId].remove();
            delete this.sitemaps3d[sitemapVersionLevelId];
        }
    }

    /**
     * Finds the above sitemap layer from the layer type and sitemapMapOrder value.
     * If there is no above layer it will return the corresponding type slot layer.
     */
    private getSitemapLayerAboveLayerId(type: LayerSpecification["type"], sitemapMapOrder: number | null) {
        // Sitemaps with a null value are always placed on the bottom of the map
        const orderValue = getSitemapMapOrderValue(sitemapMapOrder);
        const sitemapsWithHigherOrderValue = Object.values(this.sitemaps2d).filter(
            (sitemap) => sitemap.mapOrder != null && sitemap.layersAddedToMap && getSitemapMapOrderValue(sitemap.mapOrder) > orderValue
        );

        // No sitemaps are above it, return the slot layer
        if (sitemapsWithHigherOrderValue.length === 0) {
            return this.getSitemapSlotLayerForLayerType(type);
        }

        // Find the sitemap which is above it in the order
        const aboveSitemap = [...sitemapsWithHigherOrderValue].sort((a, b) => a.mapOrder - b.mapOrder)[0];

        // Find the layer with the same type
        const aboveLayer = aboveSitemap.getLayers().find((l) => l.type === type);
        return aboveLayer != null ? aboveLayer.id : this.getSitemapSlotLayerForLayerType(type);
    }

    private getSitemapSlotLayerForLayerType(type: LayerSpecification["type"]) {
        switch (type) {
            case "fill":
                return IVENTIS_SITEMAP_AREA_SLOT_ID;
            case "line":
                return IVENTIS_SITEMAP_LINE_SLOT_ID;
            case "symbol":
                return IVENTIS_SITEMAP_TEXT_SLOT_ID;
            case "raster":
                return IVENTIS_SITEMAP_RASTER_SLOT_ID;
            default:
                throw new Error(`Layer type '${type}' is not handled for sitemap ordering`);
        }
    }

    public destroy() {
        this.storeSubscription?.unsubscribe();
    }
}
